import { useCallback, useEffect, useMemo, useRef } from 'react'

import { getSingletonChannel } from '../utils/GetSingletonChannel'

export function useBroadcastChannel(channelName, onMessageReceived) {
    const channel = useMemo(() => getSingletonChannel(channelName), [channelName])

    // Keep the latest callback without re-subscribing on every render.
    // Written in an effect rather than during render, which refs disallow.
    const onMessageReceivedRef = useRef(onMessageReceived)
    useEffect(() => {
        onMessageReceivedRef.current = onMessageReceived
    }, [onMessageReceived])

    useEffect(() => {
        if (!channel) {
            console.error('Channel is not defined')
            return
        }

        const handleMessage = (event) => onMessageReceivedRef.current(event.data)
        channel.addEventListener('message', handleMessage)

        // The channel is a singleton shared across the app, so remove this
        // listener on unmount but leave the channel itself open.
        return () => channel.removeEventListener('message', handleMessage)
    }, [channel])

    const postMessage = useCallback(
        (message) => {
            try {
                channel?.postMessage(message)
            } catch (error) {
                console.error('Failed to post message:', error)
            }
        },
        [channel]
    )

    return {
        postMessage,
    }
}
